<script>
import { mapActions, mapGetters } from 'vuex'
import get from 'lodash/get'
import { formatDate } from '@lib/utils/format-date'
import formatDollar from '@lib/utils/format-dollar'
import ElasticProcessor from '../../processors/float-elastic-processor'

import ClaimStatusComponent from '../../components/claim-status'
import ClaimTopUpAmount from './components/claim-top-up-amount'
import ClaimEdit from './components/claim-edit'
import providerStatusesTable from './claim-statuses.table'
import providerTransactionsTable from './claim-transactions.table'

import FreezeEwalletModal from './components/modals/freeze-ewallet-modal'
import RemoveLimitModal from './components/modals/remove-limit-modal'
import ResendClaimModal from './components/modals/resend-claim-modal'
import RevokeClaimModal from './components/modals/revoke-claim-modal'
import ClaimOrderModal from './components/modals/claim-order-modal.vue'

export default {
  name: 'ClaimDetails',
  page: {
    title: 'Claim Details',
  },
  components: {
    ClaimStatusComponent,
    ClaimTopUpAmount,
    FreezeEwalletModal,
    RemoveLimitModal,
    ResendClaimModal,
    RevokeClaimModal,
    ClaimOrderModal,
    ClaimEdit,
  },
  data: function() {
    return {
      loading: true,
      freezing: false,
      claim: {},
      claimBalance: {},
      transactionSum: 0,
      statuses: null,
      transactions: providerTransactionsTable(this),
      clickedOrderRow: null,
      modal: {
        removeLimit: false,
        resend: false,
        revoke: false,
        freeze: false,
        order: false,
      },
    }
  },
  computed: {
    ...mapGetters('me', [
      'canFreezeEWallets',
      'canOverrideCashOut',
      'canRevoke',
      'canReinstate',
      'canTopUp',
    ]),
    id() {
      return this.$route.params.id
    },
    isEdit() {
      return this.$route.params.edit === 'edit'
    },
    isTopUpAmount() {
      return this.$route.params.edit === 'top-up-amount'
    },
    createdBy() {
      return this.userWithDate('created')
    },
    updatedBy() {
      return this.userWithDate('updated')
    },
    itce() {
      const itce = get(this.claim, 'itce', 0)
      return `${itce}%`
    },
    frozen() {
      return get(this.claim, 'frozen', false)
    },
    amount() {
      let result = formatDollar(this.claim.amount)
      if (this.claim.status === 'revoked') {
        result = '—'
      }
      return result
    },
    eWalletBalance() {
      return get(this.claimBalance, 'availableBalance', 0)
    },
    ewalletReference() {
      return get(this.claim, 'ewalletReference')
    },
    isLabelsVisible() {
      return this.claim.cashOutOverridable || this.frozen
    },
  },
  async created() {
    await this.fetchClaim()
    this.loading = false

    this.statuses = providerStatusesTable(this)

    if (this.ewalletReference) {
      this.claimTransactionsTable()
      this.fetchClaimTransactionsSum()
    }
  },
  methods: {
    ...mapActions('claims', [
      'getClaim',
      'getClaimBalance',
      'getClaimTransactionsSum',
      'claimFreezing',
    ]),
    formatDate,
    formatDollar,
    async fetchClaim() {
      const [error, data] = await this.getClaim(this.id)

      if (!error) {
        this.claim = data
      }

      this.fetchClaimBalance()
    },
    async fetchClaimBalance() {
      const [balanceError, balance] = await this.getClaimBalance(this.id)

      if (!balanceError) {
        this.claimBalance = balance
      }
    },
    claimTransactionsTable() {
      this.transactions.processor = new ElasticProcessor({
        component: this,
        path: `/claims/${this.id}/transactions`,
        staticDataQuery: {
          query: {
            bool: {
              should: [
                {
                  term: {
                    'payment_source.id': this.ewalletReference,
                  },
                }, {
                  term: {
                    'payment_destination.id': this.ewalletReference,
                  },
                },
              ],
            },
          },
        },
        initialDataQuery: {
          sort: { created_at: 'desc' },
        },
        modifier(item) {
          item.clickable = item.payment_source.id === this.ewalletReference
        },
      })
    },
    async fetchClaimTransactionsSum() {
      this.transactionSum = await this.getClaimTransactionsSum({
        claimId: this.id,
        ewalletReference: this.ewalletReference,
      })
    },
    userWithDate(key) {
      const at = get(this.claim, `${key}At`)
      const firstName = get(this.claim, `${key}By.firstName`)
      const lastName = get(this.claim, `${key}By.lastName`)
      let date

      if (at) {
        date = formatDate(at, 'DD/MM/YYYY hh:mma')
      }

      return `${firstName} ${lastName} ${date}`.trim()
    },
    isShowButton(type) {
      const { status } = this.claim

      switch (type) {
        case 'topUp':
          return this.canTopUp && status === 'redeemed'
        case 'edit':
          return this.canReinstate && ['created', 'revoked'].includes(status)
        case 'resend':
          return status === 'created'
        case 'revoke':
          return this.canRevoke && ['created', 'code_sent'].includes(status)
        case 'removeLimit':
          return (
            this.canOverrideCashOut && ['created', 'redeemed'].includes(status)
          )
        case 'freeze':
          return (
            this.canFreezeEWallets && ['created', 'redeemed'].includes(status)
          )
        default:
          return false
      }
    },
    async updateClaim(claim) {
      this.claim = {
        ...this.claim,
        ...claim,
      }

      this.statuses.processor.getData()

      if (this.ewalletReference) {
        setTimeout(() => {
          // wait for elastic update
          this.fetchClaimBalance()
          this.fetchClaimTransactionsSum()
          this.transactions.processor.getData()
        }, 2000)
      }
    },
    onUpdate(form) {
      this.updateClaim(form)
      this.onCancel()
    },
    onCancel() {
      this.$router.push({
        name: 'claims-details',
        params: { edit: null },
      })
    },
    onTopUpAmount() {
      this.$router.push({
        name: 'claims-details',
        params: { edit: 'top-up-amount' },
      })
    },
    onEdit() {
      this.$router.push({
        name: 'claims-details',
        params: { edit: 'edit' },
      })
    },
    async request(call, loadingKey) {
      this[loadingKey] = true

      const [error, data] = await call()

      if (!error) {
        this.updateClaim(data)
      } else {
        console.error(error)
      }

      this[loadingKey] = false
    },
    onResendLink() {
      this.modal.resend = true
    },
    onRevoke() {
      this.modal.revoke = true
    },
    onFreezeEWallet() {
      if (this.frozen) {
        this.request(async () => {
          return this.claimFreezing({
            id: this.id,
            payload: !this.frozen,
          })
        }, 'freezing')
      } else {
        this.modal.freeze = true
      }
    },
    onRemoveLimit() {
      this.modal.removeLimit = true
    },
    onTransactionRowClick(row) {
      const { payment_source: { id } } = row

      if (id === this.ewalletReference) {
        this.clickedOrderRow = row
        this.modal.order = true
      }
    },
    setTransactionClickable(item) {
      item.clickable = item.payment_source.id === this.ewalletReference
    },
  },
}
</script>

<template>
  <main-layout
    :title="`Claim ${claim.number}`"
    :back="{ name: 'claims' }"
    :loading="loading"
  >
    <claim-top-up-amount
      v-if="isTopUpAmount"
      :claim="claim"
      @update="onUpdate"
      @cancel="onCancel"
    />

    <claim-edit
      v-else-if="isEdit"
      :claim="claim"
      @update="onUpdate"
      @cancel="onCancel"
    />

    <template v-else>
      <data-box
        header="Category Details"
        :status="null"
        :class="$style.card"
      >
        <template slot="buttons">
          <el-button
            v-if="isShowButton('topUp')"
            :class="$style.button"
            type="primary"
            @click="onTopUpAmount"
          >
            Top up amount
          </el-button>
          <el-button
            v-if="isShowButton('edit')"
            :class="$style.button"
            type="primary"
            plain
            @click="onEdit"
          >
            Edit Claim Settlement
          </el-button>
          <el-button
            v-if="isShowButton('resend')"
            :class="$style.button"
            type="primary"
            @click="onResendLink"
          >
            Resend Registration Link
          </el-button>
          <el-button
            v-if="isShowButton('revoke')"
            :class="$style.button"
            type="danger"
            plain
            @click="onRevoke"
          >
            Revoke Claim
          </el-button>
        </template>
        <data-list>
          <dt>Claim No.</dt>
          <dd>{{ claim.number }}</dd>

          <dt>Created by</dt>
          <dd>{{ createdBy }}</dd>

          <dt>Member First Name</dt>
          <dd>{{ claim.firstName }}</dd>

          <dt>Member Last Name</dt>
          <dd>{{ claim.lastName }}</dd>

          <dt>Member Email</dt>
          <dd>{{ claim.email }}</dd>

          <dt>ITCE</dt>
          <dd>{{ itce }}</dd>

          <dt>Amount</dt>
          <dd>{{ amount }}</dd>

          <dt>Status</dt>
          <dd>
            <claim-status-component
              :value="claim.status"
              :frozen="claim.frozen"
            />
          </dd>

          <dt>Last updated</dt>
          <dd>{{ updatedBy }}</dd>
        </data-list>
      </data-box>

      <data-box
        header="Transaction History"
        :status="null"
        :class="$style.card"
      >
        <template slot="buttons">
          <el-button
            v-if="isShowButton('freeze')"
            :class="$style.button"
            type="warning"
            plain
            :loading="freezing"
            @click="onFreezeEWallet"
          >
            {{
              !frozen ? "Freeze Member's eWallet" : "Unfreeze Member's eWallet"
            }}
          </el-button>
          <el-button
            v-if="isShowButton('removeLimit')"
            :class="$style.button"
            type="danger"
            plain
            @click="onRemoveLimit"
          >
            Remove Cash Out Limit
          </el-button>
        </template>
        <data-list :class="{ [$style.list]: isLabelsVisible }">
          <dt :class="{ [$style.rowLabel]: isLabelsVisible }">
            Member’s eWallet Balance
          </dt>
          <dd :class="[$style.labels, frozen && $style.frozenValue]">
            <div :class="$style.balance">
              {{ formatDollar(eWalletBalance) }}
            </div>
            <div :class="$style.labelsWrap">
              <div
                v-if="frozen"
                :class="[$style.label, $style.frozenLabel]"
              >
                eWallet frozen
              </div>
              <div
                v-if="claim.cashOutOverridable"
                :class="[$style.label, $style.cashOutLabel]"
              >
                Cash Out Limit Removed
              </div>
            </div>
          </dd>

          <dt>Transaction Sum</dt>
          <dd>{{ formatDollar(transactionSum) }}</dd>
        </data-list>
        <table-layout
          v-if="transactions.processor"
          :table-name="transactions.tableName"
          :fragments="false"
          :processor="transactions.processor"
          :columns="transactions.columns"
          :table-controls="false"
          :class="$style.table"
          :multisort="false"
          :modifier="setTransactionClickable"
          shadow="never"
          hider
          @row-click="onTransactionRowClick"
        />
      </data-box>

      <data-box
        header="Claim Status History"
        :status="null"
        :class="$style.card"
      >
        <table-layout
          v-if="statuses && statuses.processor"
          :table-name="statuses.tableName"
          :fragments="false"
          :processor="statuses.processor"
          :columns="statuses.columns"
          :table-controls="false"
          :class="[$style.table, $style.tableStatusHistory]"
          :multisort="false"
          shadow="never"
          hider
        />
      </data-box>
    </template>

    <remove-limit-modal
      v-if="modal.removeLimit"
      :id="id"
      :cash-out-overridable="claim.cashOutOverridable"
      :visible.sync="modal.removeLimit"
      @close-modal="modal.removeLimit = false"
      @done="updateClaim"
    />

    <resend-claim-modal
      v-if="modal.resend"
      :id="id"
      :visible.sync="modal.resend"
      @close-modal="modal.resend = false"
      @done="updateClaim"
    />

    <revoke-claim-modal
      v-if="modal.revoke"
      :id="id"
      :visible.sync="modal.revoke"
      @close-modal="modal.revoke = false"
      @done="updateClaim"
    />

    <freeze-ewallet-modal
      v-if="modal.freeze"
      :id="id"
      :frozen="frozen"
      :visible.sync="modal.freeze"
      @close-modal="modal.freeze = false"
      @done="updateClaim"
    />

    <claim-order-modal
      v-if="modal.order"
      :order-row="clickedOrderRow"
      :claim-id="id"
      :visible.sync="modal.order"
      @close-modal="modal.order = false"
    />
  </main-layout>
</template>

<style lang="scss" module>
.row {
  display: flex;
}

.card {
  margin-bottom: 1.5rem;
}

.list {
  margin-top: rem(-10px);

  dd {
    width: 70% !important;
  }
}

.table {
  margin: rem(14px 0 0);
  overflow-x: hidden;
  border: none;

  [class^='table-layout'] {
    padding: 0;

    table > thead > tr > th > div,
    table > tbody > tr > td > div {
      padding: 0;
    }
  }
}

.tableStatusHistory {
  margin-top: rem(-12px);
}

.button {
  min-width: rem(230px);
}

.labels {
  display: flex;
  align-items: center;
  width: 100%;
}

.balance {
  margin-right: rem(15px);
}

.rowLabel {
  padding-top: rem(10px);
  padding-bottom: rem(10px);
}

.labelsWrap {
  display: flex;
}

.frozenValue {
  color: #fe9f1e;
}

.label {
  padding: rem(10px 20px);
  font-weight: normal;
  border-radius: rem(4px);

  &:not(:last-child) {
    margin-right: rem(10px);
  }
}

.frozenLabel,
.cashOutLabel {
  color: white;
  white-space: nowrap;
}

.frozenLabel {
  background-color: #fe9f1e;
}

.cashOutLabel {
  background-color: var(--color-error);
}
</style>
